// The file format of the word vectors search uses, and the one sum made with
// them. Pure: the build script writes the files with this and the app reads
// them with it — see scripts/build-vectors.ts and services/search-vectors.ts.
//
//   'EMV1'    4 bytes
//   dims      uint16, little-endian
//   count     uint32, how many words
//   length    uint32, bytes of the word list
//   words     UTF-8, one a line, most frequent first
//   vectors   count × dims signed bytes, in the words' order

export interface IVectors {
    words: string[]
    index: Map<string, number>
    dims: number
    vectors: Int8Array
    norms: Float32Array
}

const MAGIC = 'EMV1'
const HEAD = 14

export function writeTable(words: string[], dims: number, vectors: Int8Array): Uint8Array {
    const list = new TextEncoder().encode(words.join('\n'))
    const out = new Uint8Array(HEAD + list.length + vectors.length)
    const view = new DataView(out.buffer)
    for (let i = 0; i < 4; i++) out[i] = MAGIC.charCodeAt(i)
    view.setUint16(4, dims, true)
    view.setUint32(6, words.length, true)
    view.setUint32(10, list.length, true)
    out.set(list, HEAD)
    out.set(new Uint8Array(vectors.buffer, vectors.byteOffset, vectors.length), HEAD + list.length)
    return out
}

export function readTable(bytes: Uint8Array): IVectors {
    let magic = ''
    for (let i = 0; i < 4; i++) magic += String.fromCharCode(bytes[i])
    if (magic !== MAGIC) throw new Error('not a word vector table')
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const dims = view.getUint16(4, true)
    const count = view.getUint32(6, true)
    const length = view.getUint32(10, true)
    const words = new TextDecoder().decode(bytes.subarray(HEAD, HEAD + length)).split('\n')
    if (words.length !== count) throw new Error('word vector table is damaged')
    const vectors = new Int8Array(bytes.buffer, bytes.byteOffset + HEAD + length, count * dims)
    // Worked out once here, so a cosine is one pass over two vectors.
    const norms = new Float32Array(count)
    for (let i = 0; i < count; i++) {
        let sum = 0
        for (let d = 0; d < dims; d++) sum += vectors[i * dims + d] ** 2
        norms[i] = Math.sqrt(sum)
    }
    return { words, index: new Map(words.map((w, i) => [w, i])), dims, vectors, norms }
}

// How near two of a table's words are in meaning: the cosine of their
// vectors, from -1 to 1. The scale each vector was stored at does not matter.
export function cosine(t: IVectors, i: number, j: number): number {
    const d = t.dims
    let sum = 0
    for (let k = 0; k < d; k++) sum += t.vectors[i * d + k] * t.vectors[j * d + k]
    return t.norms[i] && t.norms[j] ? sum / (t.norms[i] * t.norms[j]) : 0
}
